import React, { useState, useEffect, useRef } from "react";
import { UnifiedDropdown } from "../../../../_shared/components";
import { MODEL_OPTIONS } from "../../../../_shared/constants";
import "./AddConfigurationModal.scss";
import { useAppSelector } from "../../../../../common/redux/core";
import { TextField } from "@mui/material";
import * as XLSX from "xlsx";
import { ToastMessageUtil } from "../../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../../common/enums/controls/toast.enum";
export interface AssignmentField {
  id: string;
  fieldName: string;
  validationRule: string;
  qualityRule: string;
  fieldContext: string;
  fieldDescription?: string;
  is_deleted?: boolean;
  ui_position_order?: number;
}

export interface ConfigurationFormData {
  fields: any;
  configurationName: string;
  flowDescription: string;
  chatEnabled: boolean;
  documentType: string;
  selectModel: string;
  assignmentFields: AssignmentField[]; // use assignmentFields consistently
}

interface AddConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ConfigurationFormData) => void;
  existingConfigNames?: string[];
  isEditMode?: boolean;
  initialData?: ConfigurationFormData;
}

const AddConfigurationModal: React.FC<AddConfigurationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingConfigNames = [],
  isEditMode = false,
  initialData
}) => {
  const { selectedDocumentType } = useAppSelector(
    (state) => state.documentType
  );

  const getDefaultFormData = (): ConfigurationFormData => ({
    configurationName: "",
    flowDescription: "",
    chatEnabled: false,
    documentType: selectedDocumentType || "Engineering",
    selectModel: "GPT 4-o",
    fields: [],
    assignmentFields: [
      {
        id: "0",
        fieldName: "",
        validationRule: "",
        qualityRule: "",
        fieldContext: "",
        fieldDescription: "",
        is_deleted: false,
        ui_position_order: 1
      }
    ]
  });

  const [formData, setFormData] = useState<ConfigurationFormData>(
    getDefaultFormData()
  );
  const [descriptionCharCount, setDescriptionCharCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [fieldHeights, setFieldHeights] = useState<{
    [key: string]: { description: number; context: number };
  }>({});
  const [dragging, setDragging] = useState<{
    fieldId: string | null;
    fieldType: "description" | "context" | null;
    startY: number;
    startHeight: number;
  }>({ fieldId: null, fieldType: null, startY: 0, startHeight: 0 });
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    content: string;
    position: { top: number; left: number };
  }>({ visible: false, content: "", position: { top: 0, left: 0 } });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [draggedFieldIndex, setDraggedFieldIndex] = useState<number | null>(
    null
  );
  const [showExcelFormatTooltip, setShowExcelFormatTooltip] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialData && !isInitialized) {
        // Map fields for form compatibility
        const mappedAssignmentFields = initialData?.fields
          ?.filter((field: any) => !field.is_deleted) // Filter out deleted fields
          ?.map((field: any) => ({
            id: field.field_id || Date.now().toString(),
            fieldName: field.field_name,
            validationRule: field.validation_rules,
            qualityRule: field.quality_rules || "",
            fieldContext: field.field_key,
            fieldDescription: field.field_description,
            is_deleted: false,
            ui_position_order: field.ui_position_order
          }))
          ?.sort(
            (a: any, b: any) =>
              (a.ui_position_order || 0) - (b.ui_position_order || 0)
          ); // Sort by ui_position_order
        setFormData({
          ...initialData,
          assignmentFields: mappedAssignmentFields || []
        });
        setDescriptionCharCount(initialData.flowDescription.length);
        setIsInitialized(true);
      } else if (!isEditMode && !isInitialized) {
        setFormData(getDefaultFormData());
        setDescriptionCharCount(0);
        setIsInitialized(true);
      }
    } else if (!isOpen && isInitialized) {
      setIsInitialized(false);
    }
  }, [isOpen, isEditMode, isInitialized, initialData]);

  useEffect(() => {
    if (isOpen && !isEditMode && isInitialized && selectedDocumentType) {
      setFormData((prev) => ({
        ...prev,
        documentType: selectedDocumentType
      }));
    }
  }, [selectedDocumentType, isOpen, isEditMode, isInitialized]);

  const maxDescriptionLength = 2000;

  const isNameDuplicate = existingConfigNames.some((name) => {
    const isSameName =
      name.toLowerCase().trim() ===
      formData.configurationName.toLowerCase().trim();

    if (isEditMode && initialData) {
      return (
        isSameName &&
        name.toLowerCase().trim() !==
          initialData.configurationName.toLowerCase().trim()
      );
    }
    return isSameName;
  });

  const isFormValid =
    formData.configurationName.trim() !== "" &&
    formData.flowDescription.trim() !== "" &&
    !isNameDuplicate;

  const handleInputChange = (
    field: keyof ConfigurationFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => {
      const updated: ConfigurationFormData = {
        ...prev,
        [field]: value
      } as ConfigurationFormData;

      // if (field === "documentType" && value !== "Others") {
      //   updated.otherDocumentName = "";
      // }
      return updated;
    });

    if (field === "flowDescription" && typeof value === "string") {
      setDescriptionCharCount(value.length);
    }
  };

  const handleFieldChange = (
    fieldId: string,
    property: keyof AssignmentField,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      assignmentFields: prev.assignmentFields.map((field) =>
        field.id === fieldId ? { ...field, [property]: value } : field
      )
    }));
  };

  const addNewField = () => {
    const tempId = `temp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const newField: AssignmentField = {
      id: tempId,
      fieldName: "",
      validationRule: "",
      qualityRule: "",
      fieldContext: "",
      fieldDescription: "",
      is_deleted: false,
      ui_position_order: formData.assignmentFields.length + 1
    };

    setFormData((prev) => ({
      ...prev,
      assignmentFields: [...prev.assignmentFields, newField]
    }));

    setFieldHeights((prev) => ({
      ...prev,
      [tempId]: { description: 44, context: 44 }
    }));
  };

  const removeField = (fieldId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignmentFields: prev.assignmentFields.map((field) =>
        field.id === fieldId ? { ...field, is_deleted: true } : field
      )
    }));

    setFieldHeights((prev) => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
  };

  const handleSave = () => {
    if (!isFormValid) {
      return;
    }

    // Remove temporary IDs (starting with "temp_") from new fields before sending to API
    const cleanedFormData = {
      ...formData,
      assignmentFields: formData.assignmentFields.map((field) => {
        const fieldId = String(field.id);
        if (fieldId.startsWith("temp_")) {
          const { id, ...fieldWithoutId } = field;
          return fieldWithoutId as AssignmentField;
        }
        return field;
      })
    };

    onSave(cleanedFormData);

    if (!isEditMode) {
      setFormData(getDefaultFormData());
      setDescriptionCharCount(0);
    }

    setIsInitialized(false);
    onClose();
  };

  const handleCancel = () => {
    setFormData(getDefaultFormData());
    setDescriptionCharCount(0);
    setFieldHeights({});
    setTooltipData({
      visible: false,
      content: "",
      position: { top: 0, left: 0 }
    });
    onClose();
  };

  const handleDragStart = (
    fieldId: string,
    fieldType: "description" | "context",
    event: React.MouseEvent
  ) => {
    event.preventDefault();
    const currentHeight = fieldHeights[fieldId]?.[fieldType] || 44;
    setDragging({
      fieldId,
      fieldType,
      startY: event.clientY,
      startHeight: currentHeight
    });
  };

  const handleDragMove = (event: MouseEvent) => {
    if (dragging.fieldId && dragging.fieldType) {
      const deltaY = event.clientY - dragging.startY;
      const newHeight = Math.max(
        44,
        Math.min(200, dragging.startHeight + deltaY)
      );

      setFieldHeights((prev) => ({
        ...prev,
        [dragging.fieldId!]: {
          ...prev[dragging.fieldId!],
          [dragging.fieldType!]: newHeight
        }
      }));
    }
  };

  const handleDragEnd = () => {
    setDragging({ fieldId: null, fieldType: null, startY: 0, startHeight: 0 });
  };

  const showTooltip = (
    content: string,
    event: React.MouseEvent<HTMLTextAreaElement>
  ) => {
    if (!content.trim()) return;

    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    const textarea = event.currentTarget;
    const rect = textarea.getBoundingClientRect();
    setTooltipData({
      visible: true,
      content,
      position: {
        top: rect.top - 20,
        left: rect.right + 10
      }
    });

    // Set timeout to hide after 3 seconds
    tooltipTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setTooltipData({
          visible: false,
          content: "",
          position: { top: 0, left: 0 }
        });
      }
    }, 3000);
  };

  const hideTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    // Add a small delay to allow user to move cursor to tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setTooltipData({
          visible: false,
          content: "",
          position: { top: 0, left: 0 }
        });
      }
    }, 200);
  };

  const handleTooltipMouseEnter = () => {
    setIsTooltipHovered(true);
    // Clear timeout when hovering over tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    // Restart timeout when leaving tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipData({
        visible: false,
        content: "",
        position: { top: 0, left: 0 }
      });
    }, 3000);
  };

  const handleFieldDragStart = (index: number) => {
    setDraggedFieldIndex(index);
  };

  const handleFieldDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedFieldIndex === null || draggedFieldIndex === index) return;

    // Get only non-deleted fields
    const activeFields = formData.assignmentFields.filter((f) => !f.is_deleted);
    const draggedField = activeFields[draggedFieldIndex];
    const targetField = activeFields[index];

    // Update all fields with new positions
    const updatedFields = formData.assignmentFields.map((field) => {
      // Skip deleted fields
      if (field.is_deleted) return field;

      if (field.id === draggedField.id) {
        return { ...field, ui_position_order: index + 1 };
      } else if (field.id === targetField.id) {
        return { ...field, ui_position_order: draggedFieldIndex + 1 };
      }

      // Recalculate positions for fields in between
      const currentActiveIndex = activeFields.findIndex(
        (f) => f.id === field.id
      );
      if (currentActiveIndex === -1) return field;

      if (draggedFieldIndex < index) {
        // Moving down
        if (
          currentActiveIndex > draggedFieldIndex &&
          currentActiveIndex <= index
        ) {
          return { ...field, ui_position_order: currentActiveIndex };
        }
      } else {
        // Moving up
        if (
          currentActiveIndex >= index &&
          currentActiveIndex < draggedFieldIndex
        ) {
          return { ...field, ui_position_order: currentActiveIndex + 2 };
        }
      }

      return { ...field, ui_position_order: currentActiveIndex + 1 };
    });

    // Sort by ui_position_order
    updatedFields.sort(
      (a, b) => (a.ui_position_order || 0) - (b.ui_position_order || 0)
    );

    setFormData((prev) => ({
      ...prev,
      assignmentFields: updatedFields
    }));

    setDraggedFieldIndex(index);
  };

  const handleFieldDragEnd = () => {
    setDraggedFieldIndex(null);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          ToastMessageUtil.show(
            "Excel file is empty or does not contain data.",
            ToastVariant.ERROR
          );
          return;
        }

        // Get headers (first row)
        const headers = (jsonData[0] as any[]).map((h) =>
          String(h).toLowerCase().trim()
        );

        // Find column indices
        const fieldNameIdx = headers.findIndex(
          (h) => h.includes("field name") || h.includes("fieldname")
        );
        const fieldDescIdx = headers.findIndex(
          (h) =>
            h.includes("field description") ||
            h.includes("fielddescription") ||
            h.includes("description")
        );
        const fieldContextIdx = headers.findIndex(
          (h) =>
            h.includes("field context") ||
            h.includes("fieldcontext") ||
            h.includes("context")
        );
        const validationIdx = headers.findIndex(
          (h) => h.includes("validation") || h.includes("validation rule")
        );
        const qualityIdx = headers.findIndex(
          (h) => h.includes("quality") || h.includes("quality rule")
        );

        if (fieldNameIdx === -1) {
          ToastMessageUtil.show(
            "Excel file must contain a 'Field Name' column. Please check the headers.",
            ToastVariant.ERROR
          );
          return;
        }

        // Parse data rows (skip header)
        const newFields: AssignmentField[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          const fieldName = row[fieldNameIdx]
            ? String(row[fieldNameIdx]).trim()
            : "";

          // Skip empty rows
          if (!fieldName) continue;

          newFields.push({
            id: `temp_${Date.now()}_${Math.random()
              .toString(36)
              .substr(2, 9)}_${i}`,
            fieldName: fieldName,
            fieldDescription:
              fieldDescIdx !== -1 && row[fieldDescIdx]
                ? String(row[fieldDescIdx]).trim()
                : "",
            fieldContext:
              fieldContextIdx !== -1 && row[fieldContextIdx]
                ? String(row[fieldContextIdx]).trim()
                : "",
            validationRule:
              validationIdx !== -1 && row[validationIdx]
                ? String(row[validationIdx]).trim()
                : "",
            qualityRule:
              qualityIdx !== -1 && row[qualityIdx]
                ? String(row[qualityIdx]).trim()
                : "",
            is_deleted: false,
            ui_position_order: i
          });
        }

        if (newFields.length === 0) {
          ToastMessageUtil.show(
            "No valid field data found in the Excel file.",
            ToastVariant.ERROR
          );
          return;
        }

        // Update existing fields or add new ones (no duplicates)
        setFormData((prev) => {
          const nonEmptyFields = prev.assignmentFields.filter(
            (field) =>
              !field.is_deleted &&
              (field.fieldName.trim() !== "" ||
                field.fieldDescription?.trim() !== "" ||
                field.fieldContext.trim() !== "" ||
                field.validationRule.trim() !== "" ||
                field.qualityRule.trim() !== "")
          );

          // Create a map of existing fields by field name (case-insensitive)
          const existingFieldsMap = new Map<string, AssignmentField>();
          nonEmptyFields.forEach((field) => {
            existingFieldsMap.set(field.fieldName.toLowerCase().trim(), field);
          });

          // Process new fields: update existing or add new
          const fieldsToAdd: AssignmentField[] = [];
          let updatedCount = 0;

          newFields.forEach((newField) => {
            const fieldNameKey = newField.fieldName.toLowerCase().trim();
            const existingField = existingFieldsMap.get(fieldNameKey);

            if (existingField) {
              // Update existing field with new data
              existingField.fieldDescription = newField.fieldDescription;
              existingField.fieldContext = newField.fieldContext;
              existingField.validationRule = newField.validationRule;
              existingField.qualityRule = newField.qualityRule;
              updatedCount++;
            } else {
              // Add as new field
              fieldsToAdd.push(newField);
            }
          });

          // Combine updated existing fields with new fields
          const combinedFields = [
            ...Array.from(existingFieldsMap.values()),
            ...fieldsToAdd
          ];

          // Recalculate ui_position_order for all fields
          const finalFields = combinedFields.map((field, index) => ({
            ...field,
            ui_position_order: index + 1
          }));

          // Show summary message
          const addedCount = fieldsToAdd.length;
          if (updatedCount > 0 && addedCount > 0) {
            ToastMessageUtil.show(
              `Successfully updated ${updatedCount} field(s) and added ${addedCount} new field(s) from Excel.`,
              ToastVariant.SUCCESS
            );
          } else if (updatedCount > 0) {
            ToastMessageUtil.show(
              `Successfully updated ${updatedCount} field(s) from Excel.`,
              ToastVariant.SUCCESS
            );
          } else {
            ToastMessageUtil.show(
              `Successfully added ${addedCount} new field(s) from Excel.`,
              ToastVariant.SUCCESS
            );
          }

          return {
            ...prev,
            assignmentFields: finalFields
          };
        });

        // Initialize field heights for new fields
        const newHeights: {
          [key: string]: { description: number; context: number };
        } = {};
        newFields.forEach((field) => {
          newHeights[field.id] = { description: 44, context: 44 };
        });
        setFieldHeights((prev) => ({ ...prev, ...newHeights }));
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        ToastMessageUtil.show(
          "Error reading Excel file. Please ensure it's a valid Excel file (.xlsx or .xls).",
          ToastVariant.ERROR
        );
      }
    };

    reader.readAsBinaryString(file);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    return () => {
      // Cleanup timeout on unmount
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (dragging.fieldId) {
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [dragging]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !isTooltipHovered
      ) {
        setTooltipData({
          visible: false,
          content: "",
          position: { top: 0, left: 0 }
        });
      }
    };

    if (tooltipData.visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tooltipData.visible, isTooltipHovered]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{isEditMode ? "Edit configuration" : "Add new configuration"}</h2>
          <button className="close-button" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Configuration Name */}
          <div className="form-field">
            <label>
              Configuration Name <span style={{ color: "red" }}>*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                value={formData.configurationName}
                onChange={(e) =>
                  handleInputChange("configurationName", e.target.value)
                }
                placeholder="Enter name"
              />
              {formData.configurationName && (
                <button
                  className="clear-button"
                  onClick={() => handleInputChange("configurationName", "")}
                >
                  ×
                </button>
              )}
            </div>
            {isNameDuplicate && formData.configurationName.trim() !== "" && (
              <div
                className="error-message"
                style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
              >
                Configuration name already exists. Please choose a different
                name.
              </div>
            )}
          </div>

          {/* Flow Description */}
          <div className="form-field">
            <label>
              Document Context <span style={{ color: "red" }}>*</span>
            </label>
            <div className="textarea-wrapper">
              <textarea
                value={formData.flowDescription}
                onChange={(e) =>
                  handleInputChange("flowDescription", e.target.value)
                }
                placeholder="Enter a description..."
                maxLength={maxDescriptionLength}
                title={formData.flowDescription || "Enter a description..."}
              />
              <div className="character-count">
                {descriptionCharCount}/{maxDescriptionLength}
              </div>
            </div>
            <div className="hint-text">This is a hint text to help users</div>
          </div>

          {/* Document Type and Select Model */}
          <div className="form-row">
            <UnifiedDropdown
              type="document-type"
              useRedux={true}
              value={formData.documentType}
              onChange={(value: string) =>
                handleInputChange("documentType", value)
              }
              label="Document Type"
              className=""
            />

            <UnifiedDropdown
              options={MODEL_OPTIONS}
              value={formData.selectModel}
              onChange={(value: string) =>
                handleInputChange("selectModel", value)
              }
              label="Select Model"
              disabled={formData.documentType === "Engineering" ? true : false}
            />
          </div>

          {/* Other Document Name - Only show when Others is selected */}
          {/* {formData.documentType === "Othersd" && (
            <div className="form-field">
              <label>
                Other document name <span style={{ color: "red" }}>*</span>
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  value={formData.otherDocumentName || ""}
                  onChange={(e) =>
                    handleInputChange("otherDocumentName", e.target.value)
                  }
                  placeholder="Enter custom document type name"
                />
                {formData.otherDocumentName && (
                  <button
                    className="clear-button"
                    onClick={() => handleInputChange("otherDocumentName", "")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          )} */}

          {/* Chat Toggle */}
          <div className="form-field chat-toggle-field">
            <div className="toggle-row-horizontal">
              <div className="toggle-item">
                <label>Chat Enabled</label>
                <div
                  className={`toggle-switch ${
                    formData.chatEnabled ? "enabled" : "disabled"
                  }`}
                  onClick={() =>
                    handleInputChange("chatEnabled", !formData.chatEnabled)
                  }
                >
                  <div className="toggle-slider"></div>
                </div>
              </div>

              {/* <div className="toggle-item">
                <label>Expose to Private</label>
                <div
                  className={`toggle-switch ${
                    formData.exposeToPrivate ? "enabled" : "disabled"
                  }`}
                  onClick={() =>
                    handleInputChange(
                      "exposeToPrivate",
                      !formData.exposeToPrivate
                    )
                  }
                >
                  <div className="toggle-slider"></div>
                </div>
              </div> */}
            </div>
          </div>

          {/* Assignment Clause */}
          {formData.documentType === "Engineering" ? null : (
            <div className="assignment-clause-section">
              <div className="assignment-header">
                <h3>Field Configuration</h3>
                <div className="header-buttons">
                  <div className="upload-button-group">
                    <button
                      className="upload-excel-button"
                      onClick={handleUploadClick}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10"></path>
                        <path d="M11.333 5.333L8 2 4.667 5.333"></path>
                        <line x1="8" y1="2" x2="8" y2="10"></line>
                      </svg>
                      Upload Excel
                    </button>
                    <button
                      className="info-icon-button"
                      onMouseEnter={() => setShowExcelFormatTooltip(true)}
                      onMouseLeave={() => setShowExcelFormatTooltip(false)}
                      type="button"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="8" cy="8" r="6"></circle>
                        <line x1="8" y1="11" x2="8" y2="8"></line>
                        <line x1="8" y1="5" x2="8.01" y2="5"></line>
                      </svg>
                    </button>
                    {showExcelFormatTooltip && (
                      <div className="excel-format-tooltip">
                        <div className="tooltip-content-line">
                          <strong>Excel Format:</strong> Field Name | Field
                          Description | Field Context | Validation Rules |
                          Quality Rules
                        </div>
                        <div className="tooltip-note">
                          Note: Only &quot;Field Name&quot; is required
                        </div>
                      </div>
                    )}
                  </div>
                  <button className="add-field-button" onClick={addNewField}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="8" y1="4" x2="8" y2="12"></line>
                      <line x1="4" y1="8" x2="12" y2="8"></line>
                    </svg>
                    Add field
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              {/* Drag hint */}
              <div className="drag-hint-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ marginRight: "6px" }}
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    stroke="#666"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <text
                    x="8"
                    y="11"
                    fontSize="10"
                    fill="#666"
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    i
                  </text>
                </svg>
                Tip: Drag and drop rows to reorder fields
              </div>

              {/* Field Headers */}
              <div className="field-headers">
                <div className="header-item">#</div>
                <div className="header-item">Field Name</div>
                <div className="header-item">Field Description</div>
                <div className="header-item">Field Context</div>
                <div className="header-item">Validation Rule</div>
                <div className="header-item">Quality Rule</div>
                <div className="header-item"></div>
                <div className="header-item" style={{ width: "40px" }}></div>
              </div>

              {/* Assignment Fields */}
              {formData.assignmentFields
                .filter((field) => !field.is_deleted)
                .map((field, index) => (
                  <div
                    key={field.id}
                    className={`assignment-field-row ${
                      draggedFieldIndex === index ? "dragging" : ""
                    }`}
                    draggable
                    onDragStart={() => handleFieldDragStart(index)}
                    onDragOver={(e) => handleFieldDragOver(e, index)}
                    onDragEnd={handleFieldDragEnd}
                    title="Drag to reorder"
                  >
                    <div className="field-number">{index + 1}</div>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        value={field.fieldName}
                        onChange={(e) =>
                          handleFieldChange(
                            field.id,
                            "fieldName",
                            e.target.value
                          )
                        }
                        placeholder="Enter name"
                      />
                      {field.fieldName && (
                        <button
                          className="clear-button"
                          onClick={() =>
                            handleFieldChange(field.id, "fieldName", "")
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="input-wrapper expandable-field">
                      <textarea
                        value={field.fieldDescription || ""}
                        onChange={(e) =>
                          handleFieldChange(
                            field.id,
                            "fieldDescription",
                            e.target.value
                          )
                        }
                        onMouseEnter={(e) =>
                          showTooltip(field.fieldDescription || "", e)
                        }
                        onMouseLeave={hideTooltip}
                        placeholder="Enter description"
                        className="expandable-textarea"
                        style={{
                          height: `${
                            fieldHeights[field.id]?.description || 44
                          }px`
                        }}
                      />
                      <div className="field-actions">
                        {field.fieldDescription && (
                          <button
                            className="clear-button-field"
                            onClick={() =>
                              handleFieldChange(
                                field.id,
                                "fieldDescription",
                                ""
                              )
                            }
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div
                        className="resize-handle"
                        onMouseDown={(e) =>
                          handleDragStart(field.id, "description", e)
                        }
                      >
                        <svg
                          width="16"
                          height="4"
                          viewBox="0 0 16 4"
                          fill="none"
                        >
                          <rect
                            width="16"
                            height="1"
                            fill="currentColor"
                            opacity="0.3"
                          />
                          <rect
                            y="3"
                            width="16"
                            height="1"
                            fill="currentColor"
                            opacity="0.3"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="input-wrapper expandable-field">
                      <textarea
                        value={field.fieldContext}
                        onChange={(e) =>
                          handleFieldChange(
                            field.id,
                            "fieldContext",
                            e.target.value
                          )
                        }
                        onMouseEnter={(e) =>
                          showTooltip(field.fieldContext || "", e)
                        }
                        onMouseLeave={hideTooltip}
                        placeholder="Enter context"
                        className="expandable-textarea"
                        style={{
                          height: `${fieldHeights[field.id]?.context || 44}px`
                        }}
                      />
                      <div className="field-actions">
                        {field.fieldContext && (
                          <button
                            className="clear-button-field"
                            onClick={() =>
                              handleFieldChange(field.id, "fieldContext", "")
                            }
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div
                        className="resize-handle"
                        onMouseDown={(e) =>
                          handleDragStart(field.id, "context", e)
                        }
                      >
                        <svg
                          width="16"
                          height="4"
                          viewBox="0 0 16 4"
                          fill="none"
                        >
                          <rect
                            width="16"
                            height="1"
                            fill="currentColor"
                            opacity="0.3"
                          />
                          <rect
                            y="3"
                            width="16"
                            height="1"
                            fill="currentColor"
                            opacity="0.3"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="input-wrapper">
                      <input
                        type="text"
                        value={field.validationRule}
                        onChange={(e) =>
                          handleFieldChange(
                            field.id,
                            "validationRule",
                            e.target.value
                          )
                        }
                        placeholder="Enter rule"
                      />
                      {field.validationRule && (
                        <button
                          className="clear-button"
                          onClick={() =>
                            handleFieldChange(field.id, "validationRule", "")
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="input-wrapper">
                      <input
                        type="text"
                        value={field.qualityRule}
                        onChange={(e) =>
                          handleFieldChange(
                            field.id,
                            "qualityRule",
                            e.target.value
                          )
                        }
                        placeholder="Enter rule"
                      />
                      {field.qualityRule && (
                        <button
                          className="clear-button"
                          onClick={() =>
                            handleFieldChange(field.id, "qualityRule", "")
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <button
                      className="delete-field-button"
                      onClick={() => removeField(field.id)}
                      disabled={formData.assignmentFields.length === 1}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M16.7749 6.88428C17.1149 6.94406 17.342 7.26811 17.2822 7.60807L15.6197 17.0633C15.6197 17.0633 15.6197 17.0633 15.6197 17.0633C15.4271 18.159 14.4753 18.9582 13.3627 18.9582H6.63728C5.52472 18.9582 4.5729 18.1591 4.38024 17.0633L2.71778 7.60807C2.658 7.2681 2.88514 6.94405 3.2251 6.88428C3.56507 6.82451 3.88912 7.05164 3.94889 7.39161L5.61136 16.8469C5.69893 17.3449 6.1316 17.7082 6.63728 17.7082H13.3627C13.8684 17.7082 14.3011 17.345 14.3886 16.8469L16.0511 7.3916C16.1109 7.05164 16.4349 6.8245 16.7749 6.88428Z"
                          fill="black"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.85417 2.2915C8.27887 2.2915 7.8125 2.75787 7.8125 3.33317V4.37484H12.1875V3.33317C12.1875 2.75787 11.7211 2.2915 11.1458 2.2915H8.85417ZM6.5625 4.37484V3.33317C6.5625 2.06752 7.58851 1.0415 8.85417 1.0415H11.1458C12.4115 1.0415 13.4375 2.06752 13.4375 3.33317V4.37484H17.5C17.8452 4.37484 18.125 4.65466 18.125 4.99984C18.125 5.34502 17.8452 5.62484 17.5 5.62484H2.5C2.15482 5.62484 1.875 5.34502 1.875 4.99984C1.875 4.65466 2.15482 4.37484 2.5 4.37484H6.5625Z"
                          fill="black"
                        />
                      </svg>
                    </button>

                    <div className="drag-handle" title="Drag to reorder">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{ cursor: "grab" }}
                      >
                        <circle cx="7" cy="5" r="1.5" fill="#999" />
                        <circle cx="13" cy="5" r="1.5" fill="#999" />
                        <circle cx="7" cy="10" r="1.5" fill="#999" />
                        <circle cx="13" cy="10" r="1.5" fill="#999" />
                        <circle cx="7" cy="15" r="1.5" fill="#999" />
                        <circle cx="13" cy="15" r="1.5" fill="#999" />
                      </svg>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="cancel-button" onClick={handleCancel}>
            Cancel
          </button>
          <button
            className={`save-button ${!isFormValid ? "disabled" : ""}`}
            onClick={handleSave}
            disabled={!isFormValid}
          >
            {isEditMode ? "Update configuration" : "Save configuration"}
          </button>
        </div>
      </div>

      {/* Tooltip */}
      {tooltipData.visible && (
        <div
          ref={tooltipRef}
          className="field-tooltip"
          style={{
            position: "fixed",
            top: `${tooltipData.position.top}px`,
            left: `${tooltipData.position.left}px`
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <div className="tooltip-content">{tooltipData.content}</div>
        </div>
      )}
    </div>
  );
};

export default AddConfigurationModal;
